import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Wallet, Plus, Trash2, Wrench, Info } from "lucide-react";
import { toast } from "sonner";
import { api, eur, dateFr, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#475569", "#EF4444", "#8B5CF6"];

const AUTRE = "Autre dépense d'entretien";

const CATEGORIES = ["Contrôle technique", "Réparation / panne", "Pneus",
  "Pièces et accessoires", "Carrosserie", "Diagnostic / expertise", "Batterie", AUTRE];

const emptyDepense = (vehicleId) => ({
  vehicle_id: vehicleId || "",
  categorie: "",
  date: new Date().toISOString().slice(0, 10),
  montant: "",
  commentaire: "",
});

export default function Budget() {
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState("");
  const [depenses, setDepenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDepense(""));
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const params = selected ? { vehicle_id: selected } : {};
    return Promise.all([
      api.get("/budget", { params }),
      api.get("/depenses", { params }),
    ]).then(([b, d]) => {
      setData(b.data);
      setDepenses(d.data);
    });
  }, [selected]);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openForm = () => {
    setForm(emptyDepense(selected || (vehicles.length === 1 ? vehicles[0].id : "")));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Sélectionnez le véhicule concerné");
    if (form.categorie === AUTRE && !form.commentaire.trim())
      return toast.error("Précisez la nature de cette dépense d'entretien");
    setSaving(true);
    try {
      await api.post("/depenses", {
        vehicle_id: form.vehicle_id,
        categorie: form.categorie,
        date: form.date,
        montant: Number(form.montant) || 0,
        commentaire: form.commentaire,
      });
      toast.success("Dépense ajoutée");
      setOpen(false);
      setForm(emptyDepense(selected));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/depenses/${id}`);
      toast.success("Dépense supprimée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!data) return <p className="text-slate-500">Chargement…</p>;

  const cards = [
    { label: "Dépenses totales", value: eur(data.total_depense) },
    { label: `Total ${data.annee_courante}`, value: eur(data.total_annee_courante) },
    { label: "Dont entretien", value: eur(data.total_entretien) },
    { label: "Autres frais d'entretien", value: eur(data.total_autres) },
  ];

  const vehiculeLabel = (v) => `${v.marque} ${v.modele}${v.immatriculation ? ` · ${v.immatriculation}` : ""}`;
  const rien = data.nb_operations === 0 && data.nb_depenses === 0;

  return (
    <div data-testid="budget-page" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Finances</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Budget</h1>
        </div>
        <Button onClick={openForm} data-testid="open-depense-form-btn"
          disabled={vehicles.length === 0}
          className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Ajouter une dépense d'entretien
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="budget-vehicle-filter" className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Véhicule
        </Label>
        <select id="budget-vehicle-filter" value={selected}
          onChange={(e) => setSelected(e.target.value)} data-testid="budget-vehicle-filter"
          className="w-full sm:w-80 h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800">
          <option value="">Tous les véhicules</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>{vehiculeLabel(v)}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={c.label} data-testid={`budget-card-${i}`}
            className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm animate-fade-up"
            style={{ animationDelay: `${i * 70}ms` }}>
            <p className="font-display text-2xl font-bold text-slate-900">{c.value}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <p data-testid="budget-info-entretien"
        className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-slate-700">
        <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
        <span>
          Les coûts saisis dans <Link to="/app/entretien" className="font-semibold text-blue-700 hover:underline">Entretien</Link>{" "}
          alimentent automatiquement ce budget : ne les ressaisissez pas ici. Utilisez « Ajouter une dépense »
          pour les autres frais d'entretien et de suivi technique : contrôle technique, pneus, réparation,
          pièces…
        </span>
      </p>

      {!selected && data.par_vehicule?.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="font-display text-xl font-semibold text-slate-800">Dépenses par véhicule</h2>
          <ul className="mt-4 space-y-2" data-testid="budget-par-vehicule">
            {data.par_vehicule.map((v) => (
              <li key={v.vehicle_id} className="flex items-center justify-between gap-3 text-sm">
                <button type="button" onClick={() => setSelected(v.vehicle_id)}
                  data-testid={`budget-vehicule-${v.vehicle_id}`}
                  className="text-slate-700 font-medium hover:text-blue-700 transition-colors text-left">
                  {v.vehicule || "Véhicule"}
                </button>
                <span className="font-semibold text-slate-900">{eur(v.montant)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {rien ? (
        <div data-testid="budget-empty" className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <Wallet className="h-8 w-8 text-slate-400 mx-auto" />
          <p className="mt-4 font-display text-lg font-semibold text-slate-800">Pas encore de dépenses</p>
          <p className="mt-1 text-sm text-slate-500">
            Saisissez une opération dans Entretien — son coût sera compté automatiquement — ou ajoutez
            ici une dépense d'entretien (contrôle technique, pneus, réparation…).
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-display text-xl font-semibold text-slate-800">Dépenses par catégorie</h2>
            <div className="h-72 mt-4" data-testid="budget-pie-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.par_categorie} dataKey="montant" nameKey="categorie"
                    innerRadius={55} outerRadius={95} paddingAngle={2}>
                    {data.par_categorie.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => eur(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-4 space-y-2">
              {data.par_categorie.map((c, i) => (
                <li key={c.categorie} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    {c.categorie}
                  </span>
                  <span className="font-semibold text-slate-800">{eur(c.montant)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-display text-xl font-semibold text-slate-800">Dépenses par année</h2>
            <div className="h-72 mt-4" data-testid="budget-bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.par_annee}>
                  <XAxis dataKey="annee" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} width={40} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Bar dataKey="montant" fill="#2563EB" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <section>
        <h2 className="font-display text-2xl font-bold text-slate-800 mb-5">Mes autres dépenses d'entretien</h2>
        {depenses.length === 0 ? (
          <div data-testid="depenses-empty" className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center">
            <p className="text-sm text-slate-500">
              Aucune dépense saisie ici. Le contrôle technique, les pneus ou une réparation s'ajoutent
              à ce budget.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="depenses-list">
            {depenses.map((d) => (
              <li key={d.id} data-testid={`depense-item-${d.id}`}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-lg font-semibold text-slate-900">{d.categorie}</p>
                  <p className="text-sm text-slate-500">
                    {dateFr(d.date)}{d.vehicule ? ` · ${d.vehicule}` : ""}
                  </p>
                  {d.commentaire && <p className="mt-1 text-sm text-slate-600">{d.commentaire}</p>}
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-display font-bold text-slate-900">{eur(d.montant)}</span>
                  <button onClick={() => remove(d.id)} data-testid={`depense-delete-${d.id}`}
                    className="text-slate-400 hover:text-red-600 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="depense-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Ajouter une dépense d'entretien</DialogTitle>
            <DialogDescription>
              Réservé aux frais d'entretien, de réparation et de suivi technique. Une opération déjà
              saisie dans Entretien est comptée automatiquement : ne la ressaisissez pas.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="depense-form">
            <div className="space-y-2">
              <Label htmlFor="depense-vehicle">Véhicule concerné</Label>
              <select id="depense-vehicle" required value={form.vehicle_id}
                onChange={set("vehicle_id")} data-testid="depense-vehicle-select"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800">
                <option value="">Choisir un véhicule…</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{vehiculeLabel(v)}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="depense-categorie">Type de dépense</Label>
                <select id="depense-categorie" required value={form.categorie}
                  onChange={set("categorie")} data-testid="depense-categorie-select"
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800">
                  <option value="">Choisir…</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="depense-montant">Montant (€)</Label>
                <Input id="depense-montant" type="number" step="0.01" min="0" required
                  value={form.montant} onChange={set("montant")}
                  data-testid="depense-montant-input" placeholder="65,90" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="depense-date">Date</Label>
                <Input id="depense-date" type="date" required value={form.date}
                  onChange={set("date")} data-testid="depense-date-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="depense-commentaire">
                  {form.categorie === AUTRE ? "Précisez la dépense" : "Note (optionnel)"}
                </Label>
                <Input id="depense-commentaire" value={form.commentaire}
                  required={form.categorie === AUTRE}
                  onChange={set("commentaire")} data-testid="depense-commentaire-input"
                  placeholder={form.categorie === AUTRE ? "Ex. reprogrammation calculateur" : "Ex. garage Dupont"} />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="depense-submit-btn"
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? "Enregistrement…" : "Enregistrer la dépense"}
            </Button>
            <p className="flex items-start gap-2 text-xs text-slate-500">
              <Wrench className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Une vidange ou des plaquettes déjà notées dans Entretien sont déjà comptées : les saisir
              ici les compterait deux fois.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
